import React, { useState, useEffect } from "react";
import { db } from "../utils/firebase";
import {
  collection,
  Timestamp,
  addDoc,
  setDoc,
  doc,
  getDoc,
  getDocs,
  doc as firestoreDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Spinner } from "./index.js";
import {
  PlusCircle,
  User,
  Home,
  AlertCircle,
  Pill,
  Scale,
  X,
  ChevronDown,
  GraduationCap,
} from "lucide-react";
import { useAuth } from "../hooks/AuthenticationProvider";
import { calculateProratedRent } from "../utils/rentCalculations";
import {
  medications,
  findMedicationSuggestions,
} from "../utils/medicationList";

const IntakeForm = ({ onNewResident }) => {
  const { organizationId, organizationData, user, isLoading, checkFeature } =
    useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newReferral, setNewReferral] = useState("");
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const [phases, setPhases] = useState([]);
  const [createUserAccount, setCreateUserAccount] = useState(false);
  const [residentDashboardCount, setResidentDashboardCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [calculationDetails, setCalculationDetails] = useState(null);
  const [isReferralDropdownOpen, setIsReferralDropdownOpen] = useState(false);
  const [medicationSuggestions, setMedicationSuggestions] = useState([]);
  const [activeMedicationIndex, setActiveMedicationIndex] = useState(null);
  const initialState = {
    firstName: "",
    lastName: "",
    ecEmail: "",
    poEmail: "",
    fullName: "",
    dob: "",
    phoneNumber: "",
    house: "",
    rentAmount: 0,
    rentSchedule: "",
    workOnEntry: "",
    rentDueDate: "",
    intakeDate: "",
    employmentHistory: [],
    ecFirstName: "",
    email: "",
    workStatus: "",
    gender: "",
    veteranStatus: "",
    phase: "",
    ecLastName: "",
    ecRelationship: "",
    ecPhoneNumber: "",
    medications: [{ name: "", dose: "" }],
    drugOfChoice: "",
    sobrietyDate: "",
    referralLocation: "",
    poFirstName: "",
    poLastName: "",
    county: "",
    pendingCharges: "",
    isMAT: false,
    balance: 0,
    currentResident: true,
    bedLocation: "",
    uaCount: 3,
    isWaitlist: false,
    dateAdded: null,
    status: "pending",
  };

  const [state, setState] = useState({ formData: initialState });
  const { residentId } = useParams();
  const drugList = [
    "THC",
    "Alcohol",
    "Fentanyl",
    "Cocaine",
    "Crack",
    "Meth",
    "Amphetamines",
    "Cough Syrup",
  ];
  const workStatusOptions = ["Full-Time", "Part-Time", "Retired", "Unemployed"];
  const [referralOptions, setReferralOptions] = useState([]);

  const genderOptions = ["Male", "Female", "Trans", "Other"];
  const veteranStatusOptions = ["Veteran", "No military background"];

  useEffect(() => {
    if (residentId && organizationId) {
      const fetchResidentData = async () => {
        const residentRef = doc(
          db,
          `organizations/${organizationId}/residents`,
          residentId
        );
        const residentSnapshot = await getDoc(residentRef);
        if (residentSnapshot.exists()) {
          setState({ formData: residentSnapshot.data() });
        }
      };
      fetchResidentData();
    }
  }, [residentId, organizationId]);

  useEffect(() => {
    const loadReferralOptions = async () => {
      if (!organizationId) return;

      try {
        const settingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );
        const settingsSnap = await getDoc(settingsRef);

        // Check if the document exists AND has a referrals array
        if (settingsSnap.exists() && settingsSnap.data().referrals) {
          // Simply set the existing referrals array
          setReferralOptions(settingsSnap.data().referrals);
        } else {
          // Only set defaults if no referrals exist
          const defaultOptions = ["Self", "Jail", "Prison", "Respite", "Other"];
          await updateDoc(settingsRef, {
            referrals: defaultOptions,
          });
          setReferralOptions(defaultOptions);
        }
      } catch (error) {
        console.error("Error loading referral options:", error);
        // We can keep this fallback for error cases
        setReferralOptions(["Self", "Jail", "Prison", "Respite", "Other"]);
      }
    };

    loadReferralOptions();
  }, [organizationId]);

  // Fetch phases from the phase collection
  useEffect(() => {
    const fetchPhases = async () => {
      if (!organizationId) {
        console.log("No organizationId, skipping phase fetch");
        return;
      }

      // Check phases feature - using checkFeature from props instead of calling useAuth() again
      if (!checkFeature("phases")) {
        console.log("Phases feature disabled, skipping phase fetch");
        return;
      }

      try {
        console.log(`Fetching phases for organization: ${organizationId}`);
        const phasesRef = collection(
          db,
          `organizations/${organizationId}/phases`
        );
        const phaseSnapshot = await getDocs(phasesRef);

        console.log(`Got ${phaseSnapshot.size} phase documents`);

        if (!phaseSnapshot.empty) {
          const phaseData = phaseSnapshot.docs.map((doc) => {
            console.log(`Phase document: ${doc.id}`, doc.data());
            return {
              id: doc.id,
              name: doc.data().name,
            };
          });
          console.log("Setting phases state:", phaseData);
          setPhases(phaseData);
        } else {
          console.log("No phase documents found");
        }
      } catch (error) {
        console.error("Error loading phases:", error);
      }
    };

    fetchPhases();
  }, [organizationId, checkFeature]);

  useEffect(() => {
    if (
      !state.formData.house ||
      !state.formData.intakeDate ||
      !state.formData.rentDueDate ||
      !state.formData.rentSchedule
    ) {
      return;
    }

    try {
      const selectedHouse = organizationData?.housesData[state.formData.house];
      let houseFee = selectedHouse?.fee || 0;
      const moveInFee =
        parseInt(organizationData?.houseSettings?.moveInFee, 10) || 0;
      const rentSchedule = state.formData.rentSchedule;

      // Convert weekly fee to monthly if needed
      if (rentSchedule === "monthly") {
        houseFee = Number((houseFee * 4.33).toFixed(2));
      }

      const calculation = calculateProratedRent(
        state.formData.intakeDate,
        houseFee,
        moveInFee,
        rentSchedule,
        state.formData.rentDueDate // Pass the full date string instead of just the day
      );

      setCalculationDetails(calculation);
      setState((prev) => ({
        ...prev,
        formData: {
          ...prev.formData,
          balance: calculation.totalBalance,
        },
      }));
    } catch (error) {
      console.error("Error in calculation setup:", error);
    }
  }, [
    state.formData.intakeDate,
    state.formData.house,
    state.formData.rentDueDate,
    state.formData.rentSchedule,
    organizationData,
  ]);

  const fetchUserData = async () => {
    if (!organizationId) return;

    try {
      // Fetch resident dashboard count
      const houseSettingsRef = firestoreDoc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      const snapshot = await getDoc(houseSettingsRef);
      if (snapshot.exists()) {
        setResidentDashboardCount(
          snapshot.data().paidResDashboardCount ||
            snapshot.data().residentDashboardCount ||
            10
        );
      }

      // Fetch existing users
      const usersQuery = collection(
        db,
        "organizations",
        organizationId,
        "users"
      );
      const usersSnapshot = await getDocs(usersQuery);
      const fetchedUsers = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(fetchedUsers);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  // 4. Add this useEffect to fetch user data when component mounts (add with your other useEffects)
  useEffect(() => {
    if (organizationId) {
      fetchUserData();
    }
  }, [organizationId]);

  // 5. Add these utility functions (add near your other functions)
  const normalizeEmail = (email) => {
    return email?.toLowerCase().trim() || "";
  };

  const capitalizeFirstLetter = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase() || "";
  };

  // 6. Add this function to create user account (add near your other functions)
  const createResidentUser = async (residentData) => {
    if (!organizationId || !residentData.email) return false;

    try {
      // Normalize email and capitalize names
      const normalizedEmail = normalizeEmail(residentData.email);
      const capitalizedFirstName = capitalizeFirstLetter(
        residentData.firstName
      );
      const capitalizedLastName = capitalizeFirstLetter(residentData.lastName);

      // Check for duplicate users using normalized email
      const isDuplicateUser = users.some(
        (user) => normalizeEmail(user.email) === normalizedEmail
      );

      if (isDuplicateUser) {
        alert("User with this email already exists.");
        return false;
      }

      // Check resident dashboard limit
      const currentResidentCount = users.filter(
        (user) => user.role === "Resident"
      ).length;
      const maxResidents = residentDashboardCount || 10;

      if (currentResidentCount >= maxResidents) {
        alert(
          `Cannot create user account. Your resident limit is ${maxResidents} residents.`
        );
        return false;
      }

      // Create user document
      const userRef = firestoreDoc(
        db,
        "organizations",
        organizationId,
        "users",
        normalizedEmail
      );

      await setDoc(userRef, {
        email: normalizedEmail,
        firstName: capitalizedFirstName,
        lastName: capitalizedLastName,
        role: "Resident",
        addedBy: user.email,
        createdAt: new Date(),
        createdDuringIntake: true, // Flag to indicate this was created during intake
      });

      console.log(
        `Created user account for ${capitalizedFirstName} ${capitalizedLastName}`
      );
      return true;
    } catch (error) {
      console.error("Error creating user account:", error);
      alert("Error creating user account: " + error.message);
      return false;
    }
  };

  const handleAddReferralOption = async (newOption) => {
    if (!newOption || referralOptions.includes(newOption)) return;

    try {
      const settingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      await updateDoc(settingsRef, {
        referrals: arrayUnion(newOption),
      });

      const updatedSnap = await getDoc(settingsRef);
      if (updatedSnap.exists()) {
        setReferralOptions(updatedSnap.data().referrals);
        setState((prevState) => ({
          ...prevState,
          formData: {
            ...prevState.formData,
            referralLocation: newOption,
          },
        }));
      }
    } catch (error) {
      console.error("Error adding referral option:", error);
    }
  };

  const handleDeleteReferralOption = async (sourceToDelete) => {
    if (
      window.confirm(
        "Are you sure you want to remove this referral source from the list?"
      )
    ) {
      try {
        const settingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );

        // Get current array and filter out the source to delete
        const updatedSources = referralOptions.filter(
          (source) => source !== sourceToDelete
        );

        // Update Firestore
        await updateDoc(settingsRef, {
          referrals: updatedSources,
        });

        // Update local state
        setReferralOptions(updatedSources);

        // If the deleted source was selected, clear the selection
        if (state.formData.referralLocation === sourceToDelete) {
          setState((prevState) => ({
            ...prevState,
            formData: {
              ...prevState.formData,
              referralLocation: "",
            },
          }));
        }
      } catch (error) {
        console.error("Error removing referral source:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const baseName = `${state.formData.firstName} ${state.formData.lastName}`;

    try {
      const collectionName = state.formData.isWaitlist
        ? "waitlist"
        : "residents";
      const collectionPath = `organizations/${organizationId}/${collectionName}`;

      // Get all residents with the same base name (with or without counter)
      const residentsRef = collection(db, collectionPath);
      const querySnapshot = await getDocs(residentsRef);

      // Find all matching residents and determine the highest counter
      let highestCounter = 0;
      querySnapshot.docs.forEach((doc) => {
        const resident = doc.data();
        const fullName = resident.fullName || "";

        // Check exact match or match with counter pattern
        if (fullName === baseName) {
          highestCounter = Math.max(highestCounter, 1);
        } else {
          // Check for name with counter pattern
          const match = fullName.match(
            new RegExp(`^${baseName} \\((\\d+)\\)$`)
          );
          if (match) {
            const counter = parseInt(match[1], 10);
            highestCounter = Math.max(highestCounter, counter);
          }
        }
      });

      // Determine the new full name
      let fullName = baseName;
      if (highestCounter > 0) {
        fullName = `${baseName} (${highestCounter + 1})`;
      }

      // Ensure rentDueDate is taken from user input
      let rentDueDate = state.formData.rentDueDate
        ? Timestamp.fromDate(new Date(state.formData.rentDueDate))
        : null;

      const houseSettingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      const houseSettingsSnap = await getDoc(houseSettingsRef);

      let is90n90 = false;
      let meetingRequirements = 0;

      if (houseSettingsSnap.exists()) {
        const houseSettingsData = houseSettingsSnap.data();
        is90n90 = houseSettingsData.is90n90 || false;
        meetingRequirements = is90n90
          ? 7
          : houseSettingsData.meetingRequirements || 0;
      } else {
        console.warn(
          "House settings not found. Defaulting is90n90 to false and meetingRequirements to 0."
        );
      }

      let formDataToSubmit = {
        ...state.formData,
        fullName: fullName,
        dateAdded: Timestamp.now(),
        rentDueDate: rentDueDate, // Use user-provided rentDueDate
        is90n90: is90n90,
        meetingRequirements: meetingRequirements,
      };

      if (state.formData.isWaitlist) {
        delete formDataToSubmit.currentResident;
        delete formDataToSubmit.bedLocation;
        delete formDataToSubmit.balance;
        delete formDataToSubmit.uaCount;
        formDataToSubmit.status = "pending";
      } else {
        formDataToSubmit.currentResident = true;
      }

      // Create user account if checkbox is checked and email exists
      if (createUserAccount && state.formData.email) {
        const userCreated = await createResidentUser(state.formData);
        if (!userCreated) {
          // If user creation failed, ask if they want to continue without creating user
          const continueWithoutUser = window.confirm(
            "Failed to create user account. Would you like to continue with the intake process without creating a user account?"
          );
          if (!continueWithoutUser) {
            return; // Stop the intake process
          }
        }
      }

      await addDoc(collection(db, collectionPath), formDataToSubmit);
      setState({ formData: initialState });
      setCreateUserAccount(false); // Reset checkbox

      // Show success message
      let successMessage = "Intake form submitted successfully!";
      if (createUserAccount && state.formData.email) {
        successMessage += " User account created.";
      }
      alert(successMessage);

      navigate(state.formData.isWaitlist ? "/waitlist" : "/beds");
      if (onNewResident) onNewResident(formDataToSubmit);
    } catch (err) {
      console.error("Error saving resident data:", err);
      alert("Error during intake process: " + err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "balance") {
      setState((prevState) => ({
        ...prevState,
        formData: { ...prevState.formData, [name]: Number(value) || 0 },
      }));
      return;
    }

    if (name === "workStatus") {
      // When workStatus is changed, also update workOnEntry if it's still empty
      // This ensures workOnEntry is set only once during the initial intake
      setState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [name]: value,
          // Only set workOnEntry if it hasn't been set yet (empty string)
          workOnEntry:
            prevState.formData.workOnEntry === ""
              ? value
              : prevState.formData.workOnEntry,
        },
      }));
    } else if (name === "house") {
      const selectedHouse = organizationData?.housesData?.[value];
      const weeklyFee = selectedHouse?.fee || 0;
      const currentSchedule = state.formData.rentSchedule;

      setState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [name]: value,
          rentAmount:
            currentSchedule === "monthly"
              ? Number((weeklyFee * 4.33).toFixed(2))
              : weeklyFee,
        },
      }));
    } else if (name === "rentSchedule") {
      const currentHouse = organizationData?.housesData?.[state.formData.house];
      const weeklyFee = currentHouse?.fee || 0;

      setState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [name]: value,
          rentAmount:
            value === "monthly"
              ? Number((weeklyFee * 4.33).toFixed(2))
              : weeklyFee,
        },
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        formData: { ...prevState.formData, [name]: value },
      }));
    }
  };

  const handleAddMedication = () => {
    setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        medications: [
          ...prevState.formData.medications,
          { name: "", dose: "" },
        ],
      },
    }));
  };

  const handleMATChange = (e) => {
    const isMATValue = e.target.value === "true"; // Convert string to boolean
    setState({
      ...state,
      formData: {
        ...state.formData,
        isMAT: isMATValue,
      },
    });
  };

  const handleMedicationNameChange = (e, index) => {
    const value = e.target.value;
    const medications = [...state.formData.medications];
    medications[index] = {
      ...medications[index],
      name: value,
    };
    setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        medications,
      },
    }));

    // Get suggestions
    if (value.length > 2) {
      const suggestions = findMedicationSuggestions(value);
      setMedicationSuggestions(suggestions);
      setActiveMedicationIndex(index);
    } else {
      setMedicationSuggestions([]);
      setActiveMedicationIndex(null);
    }
  };

  // Add this function to handle selecting a suggestion
  const handleSelectSuggestion = (suggestion, index) => {
    const medications = [...state.formData.medications];
    medications[index] = {
      ...medications[index],
      name: `${suggestion.brandName} (${suggestion.genericName})`,
    };
    setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        medications,
      },
    }));
    setMedicationSuggestions([]);
    setActiveMedicationIndex(null);
  };

  const handleWaitlistChange = (e) => {
    setState({
      ...state,
      formData: {
        ...state.formData,
        isWaitlist: e.target.checked,
      },
    });
  };

  const handleDrugChoiceChange = (event) => {
    const { value, checked } = event.target;
    let newDrugChoices = [...state.formData.drugOfChoice];

    if (checked) newDrugChoices.push(value);
    else newDrugChoices = newDrugChoices.filter((drug) => drug !== value);

    setState({
      ...state,
      formData: {
        ...state.formData,
        drugOfChoice: newDrugChoices,
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title> &bull; New Resident</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects - lowest z-index */}
        <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                New Resident Intake
              </h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Form Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Resident Information Card */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <User className="text-blue-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Resident Information
                    </h2>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      id="firstName"
                      type="text"
                      name="firstName"
                      value={state.formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="First Name"
                    />
                  </div>

                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      id="lastName"
                      type="text"
                      name="lastName"
                      value={state.formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="Last Name"
                    />
                  </div>

                  {/* New email field */}
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={state.formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="Email Address"
                    />
                  </div>

                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      id="phoneNumber"
                      type="tel"
                      name="phoneNumber"
                      value={state.formData.phoneNumber}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="Phone Number"
                    />
                  </div>

                  {/* New work status dropdown */}
                  <div className="relative" style={{ zIndex: 50 }}>
                    <select
                      id="workStatus"
                      name="workStatus"
                      value={state.formData.workStatus}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white placeholder-slate-400 transition-colors"
                    >
                      <option value="">Work Status</option>
                      {workStatusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="relative" style={{ zIndex: 50 }}>
                    <label
                      htmlFor="dob"
                      className="block text-sm font-medium text-slate-300 pl-2"
                    >
                      Date of Birth
                    </label>
                    <input
                      id="dob"
                      type="date"
                      name="dob"
                      value={state.formData.dob}
                      onChange={handleChange}
                      className="w-full pl-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white placeholder-slate-400 transition-colors"
                    />
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <select
                      id="gender"
                      name="gender"
                      value={state.formData.gender}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white placeholder-slate-400 transition-colors"
                    >
                      <option value="">Select gender</option>
                      {genderOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <select
                      id="veteranStatus"
                      name="veteranStatus"
                      value={state.formData.veteranStatus}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white placeholder-slate-400 transition-colors"
                    >
                      <option value="">Select veteran status</option>
                      {veteranStatusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Emergency Contact Card */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-lg">
                      <AlertCircle className="text-red-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Emergency Contact
                    </h2>
                  </div>
                </div>
                <div className="p-6 space-y-5">
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="text"
                      name="ecFirstName"
                      value={state.formData.ecFirstName}
                      onChange={handleChange}
                      className="w-full pt-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-red-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="First Name"
                    />
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="text"
                      name="ecLastName"
                      value={state.formData.ecLastName}
                      onChange={handleChange}
                      className="w-full pt-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-red-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="Last Name"
                    />
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="text"
                      name="ecRelationship"
                      value={state.formData.ecRelationship}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-red-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="Relationship"
                    />
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="tel"
                      name="ecPhoneNumber"
                      value={state.formData.ecPhoneNumber}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-red-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="Phone Number"
                    />
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="email"
                      name="ecEmail"
                      value={state.formData.ecEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-red-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="Email Address"
                    />
                  </div>
                </div>
              </div>

              {/* Legal Information Card */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/10 rounded-lg">
                      <Scale className="text-amber-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Legal Information
                    </h2>
                  </div>
                </div>
                <div className="p-6 space-y-4 relative z-10">
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="text"
                      name="poFirstName"
                      value={state.formData.poFirstName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-amber-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="PO First Name"
                    />
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="text"
                      name="poLastName"
                      value={state.formData.poLastName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-amber-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="PO Last Name"
                    />
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="text"
                      name="county"
                      value={state.formData.county}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-amber-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="County"
                    />
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="text"
                      name="pendingCharges"
                      value={state.formData.pendingCharges}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-amber-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="Pending Charges"
                    />
                  </div>
                  <div className="relative" style={{ zIndex: 50 }}>
                    <input
                      type="email"
                      name="poEmail"
                      value={state.formData.poEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-amber-500 text-white placeholder-slate-400 transition-colors"
                      placeholder="PO Email Address"
                    />
                  </div>
                </div>
              </div>

              {/* Medication Card */}
              <div
                className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl 
                overflow-visible   /* ⬅ allow dropdown to spill outside */
                hover:shadow-2xl transition-all duration-500"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                      <Pill className="text-emerald-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Medication &amp; MAT
                    </h2>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {state.formData.medications.map((medication, index) => (
                    <div key={index} className="flex gap-4 relative">
                      {/* ── Medication name ─────────────────────────────────── */}
                      <div className="relative flex-1">
                        <input
                          type="text"
                          name="name"
                          value={medication.name}
                          onChange={(e) => handleMedicationNameChange(e, index)}
                          onBlur={() =>
                            setTimeout(() => {
                              setMedicationSuggestions([]);
                              setActiveMedicationIndex(null);
                            }, 200)
                          }
                          className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg 
                       focus:outline-none focus:border-emerald-500 text-white placeholder-slate-400 
                       transition-colors"
                          placeholder="Medication Name"
                        />

                        {/* suggestions */}
                        {medicationSuggestions.length > 0 &&
                          activeMedicationIndex === index && (
                            <div
                              className="absolute top-full left-0 w-full mt-1 bg-slate-800 border border-slate-700 
                            rounded-lg shadow-lg z-[9999]"
                            >
                              {" "}
                              {/* ⬅ high z-index */}
                              {medicationSuggestions.map((suggestion, idx) => (
                                <div
                                  key={idx}
                                  onClick={() =>
                                    handleSelectSuggestion(suggestion, index)
                                  }
                                  className="px-4 py-2 hover:bg-slate-700 cursor-pointer text-white"
                                >
                                  <div className="font-semibold">
                                    {suggestion.brandName} (
                                    {suggestion.genericName})
                                  </div>
                                  <div className="text-sm text-slate-400">
                                    {suggestion.type}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                      </div>

                      {/* ── Dose ────────────────────────────────────────────── */}
                      <div className="relative w-32">
                        <input
                          type="text"
                          name="dose"
                          value={medication.dose}
                          onChange={(e) => {
                            const medications = [...state.formData.medications];
                            medications[index] = {
                              ...medications[index],
                              dose: e.target.value,
                            };
                            setState((prev) => ({
                              ...prev,
                              formData: { ...prev.formData, medications },
                            }));
                          }}
                          className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg 
                       focus:outline-none focus:border-emerald-500 text-white placeholder-slate-400 
                       transition-colors"
                          placeholder="Dose"
                        />
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={handleAddMedication}
                    className="relative w-full p-2 flex items-center justify-center gap-2 
                 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg transition-colors"
                  >
                    <PlusCircle size={20} />
                    Add Medication
                  </button>

                  {/* ── MAT Program radio ─────────────────────────────────── */}
                  <div className="mt-6 relative">
                    <h3 className="text-white mb-4">MAT Program</h3>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-slate-300">
                        <input
                          type="radio"
                          name="isMAT"
                          value="true"
                          checked={state.formData.isMAT === true}
                          onChange={handleMATChange}
                          className="text-emerald-500 focus:ring-emerald-500"
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-2 text-slate-300">
                        <input
                          type="radio"
                          name="isMAT"
                          value="false"
                          checked={state.formData.isMAT === false}
                          onChange={handleMATChange}
                          className="text-emerald-500 focus:ring-emerald-500"
                        />
                        No
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recovery Info Card */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-violet-500/10 rounded-lg">
                      <GraduationCap className="text-violet-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Recovery Information
                    </h2>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="relative">
                    {/* Dropdown Button */}
                    <button
                      type="button"
                      onClick={() =>
                        setIsReferralDropdownOpen(!isReferralDropdownOpen)
                      }
                      className="w-full flex items-center justify-between px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-violet-500 text-white transition-colors"
                    >
                      <span>
                        {state.formData.referralLocation ||
                          "Select referral location"}
                      </span>
                      <ChevronDown className="w-5 h-5 text-white" />
                    </button>

                    {/* Referral Options List */}
                    {isReferralDropdownOpen && (
                      <ul
                        className="absolute top-full left-0 w-full bg-slate-800 border border-slate-700 rounded-lg mt-1"
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          zIndex: 9999, // Ensure this is above all other elements
                          position: "absolute", // Ensure absolute positioning
                        }}
                      >
                        {referralOptions.map((option) => (
                          <li
                            key={option}
                            className="flex justify-between items-center px-4 py-2 hover:bg-slate-700"
                          >
                            <span
                              className="text-white cursor-pointer"
                              onClick={() => {
                                setState((prevState) => ({
                                  ...prevState,
                                  formData: {
                                    ...prevState.formData,
                                    referralLocation: option,
                                  },
                                }));
                                setIsReferralDropdownOpen(false);
                              }}
                            >
                              {option}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDeleteReferralOption(option)}
                              className="p-1 hover:bg-red-500/20 rounded-full"
                            >
                              <X className="w-4 h-4 text-red-400 hover:text-red-500" />
                            </button>
                          </li>
                        ))}
                        <li
                          className="flex justify-center items-center px-4 py-2 hover:bg-slate-700 cursor-pointer text-slate-400 italic"
                          onClick={() => {
                            setIsModalOpen(true);
                            setIsReferralDropdownOpen(false);
                          }}
                        >
                          + Add new referral source
                        </li>
                      </ul>
                    )}
                  </div>

                  <div className="relative" style={{ zIndex: 50 }}>
                    <label
                      htmlFor="date"
                      className="block text-sm font-medium text-slate-300 pl-2"
                    >
                      Sobriety Date
                    </label>
                    <input
                      type="date"
                      name="sobrietyDate"
                      value={state.formData.sobrietyDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-violet-500 text-white placeholder-slate-400 transition-colors"
                    />
                  </div>

                  {/* Phase Field - only shown if phases feature is enabled */}
                  {/* Phase Field - only shown if phases feature is enabled */}
                  {checkFeature("phases") && (
                    <div className="relative" style={{ zIndex: 50 }}>
                      <label
                        htmlFor="phase"
                        className="block text-sm font-medium text-slate-300 pl-2"
                      >
                        Recovery Phase
                      </label>
                      <select
                        id="phase"
                        name="phase"
                        value={state.formData.phase}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-violet-500 text-white placeholder-slate-400 transition-colors"
                      >
                        <option value="">Select phase</option>
                        {phases && phases.length > 0 ? (
                          phases.map((phase) => (
                            <option key={phase.id} value={phase.name}>
                              {phase.name}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>
                            No phases available
                          </option>
                        )}
                      </select>
                    </div>
                  )}

                  <div className="space-y-2 relative" style={{ zIndex: 50 }}>
                    <h3 className="text-white">Drugs of Choice</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {drugList.map((drug) => (
                        <label
                          key={drug}
                          className="flex items-center gap-2 text-slate-300"
                        >
                          <input
                            type="checkbox"
                            name="drugOfChoice"
                            value={drug}
                            checked={state.formData.drugOfChoice.includes(drug)}
                            onChange={handleDrugChoiceChange}
                            className="rounded text-violet-500 focus:ring-violet-500"
                          />
                          {drug}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Housing Details Card */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Home className="text-blue-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Housing Details
                    </h2>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="relative" style={{ zIndex: 50 }}>
                    <select
                      name="house"
                      value={state.formData.house}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white transition-colors"
                    >
                      <option value="">Select a house</option>
                      {Object.entries(organizationData?.housesData || {}).map(
                        ([name]) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div className="relative" style={{ zIndex: 50 }}>
                    <select
                      name="rentSchedule"
                      value={state.formData.rentSchedule}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white transition-colors"
                    >
                      <option value="">Select a schedule</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {/* Combined date fields in a grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative" style={{ zIndex: 50 }}>
                      <label
                        htmlFor="intakeDate"
                        className="block text-sm font-medium text-slate-300 pl-2"
                      >
                        Intake Date
                      </label>
                      <input
                        type="date"
                        name="intakeDate"
                        value={state.formData.intakeDate}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors text-sm"
                        placeholder="mm/dd/yyyy"
                      />
                    </div>
                    <div className="relative" style={{ zIndex: 50 }}>
                      <label
                        htmlFor="rentDueDate"
                        className="block text-sm font-medium text-slate-300 pl-2"
                      >
                        Next Rent Due Date
                      </label>
                      <input
                        type="date"
                        name="rentDueDate"
                        value={state.formData.rentDueDate}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors text-sm"
                        placeholder="mm/dd/yyyy"
                      />
                    </div>
                  </div>

                  {/* Combined rent amount and balance fields in a grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative" style={{ zIndex: 50 }}>
                      <label
                        htmlFor="rentAmount"
                        className="block text-sm font-medium text-slate-300 pl-2"
                      >
                        Rent Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="rentAmount"
                        name="rentAmount"
                        value={Number(state.formData.rentAmount).toFixed(2)}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors"
                      />
                    </div>

                    <div className="relative" style={{ zIndex: 50 }}>
                      <label
                        htmlFor="balance"
                        className="block text-sm font-medium text-slate-300 pl-2"
                      >
                        Starting Balance
                      </label>
                      <input
                        type="number"
                        id="balance"
                        name="balance"
                        value={state.formData.balance}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors"
                        placeholder="Enter balance"
                      />
                    </div>
                  </div>

                  {/* Cost breakdown section */}
                  {state.formData.house && (
                    <div
                      className="bg-slate-800/50 rounded-lg text-sm text-slate-300 relative"
                      style={{ zIndex: 50 }}
                    >
                      <button
                        type="button"
                        onClick={() => setIsBreakdownOpen(!isBreakdownOpen)}
                        className="w-full p-4 flex justify-between items-center hover:bg-slate-800/30 transition-colors relative z-50"
                      >
                        <span className="font-medium">Cost Breakdown</span>
                        <ChevronDown
                          className={`w-5 h-5 transition-transform ${
                            isBreakdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {isBreakdownOpen && calculationDetails?.breakdown && (
                        <div className="p-4 space-y-2 border-t border-slate-700 relative z-50">
                          <p>
                            Move-in Fee: $
                            {calculationDetails.breakdown.moveInFee?.toFixed(
                              2
                            ) || "0.00"}
                          </p>
                          <p>
                            Daily Rate: $
                            {calculationDetails.breakdown.dailyRate.toFixed(2)}
                          </p>
                          <p>
                            Prorated Rent: $
                            {calculationDetails.breakdown.proratedRent.toFixed(
                              2
                            )}
                          </p>
                          <p>
                            Days Until Due:{" "}
                            {calculationDetails.breakdown.daysUntilDue}
                          </p>
                          <p>
                            Next Due Date:{" "}
                            {calculationDetails.breakdown.nextDueDate.toLocaleDateString()}
                          </p>
                          {calculationDetails.breakdown.isFullPeriod && (
                            <p className="text-cyan-400">
                              Full{" "}
                              {organizationData?.houseSettings?.rentSchedule}{" "}
                              period
                            </p>
                          )}
                          <p className="text-white font-semibold">
                            Total Due: $
                            {Math.abs(
                              calculationDetails?.totalBalance || 0
                            ).toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden">
              <div className="p-6 space-y-4">
                {/* User Creation Checkbox - only show if email exists */}
                {state.formData.email && (
                  <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="createUserAccount"
                        checked={createUserAccount}
                        onChange={(e) => setCreateUserAccount(e.target.checked)}
                        className="w-4 h-4 text-cyan-600 bg-slate-800 border-slate-600 rounded focus:ring-cyan-500 focus:ring-2"
                      />
                      <label
                        htmlFor="createUserAccount"
                        className="text-sm font-medium text-slate-300 cursor-pointer"
                      >
                        Add as a resident user
                      </label>
                    </div>
                    <p className="text-xs text-slate-400 mt-2 ml-7">
                      This will create a user account for{" "}
                      {state.formData.firstName} {state.formData.lastName} with
                      email: {state.formData.email}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 ml-7">
                      Current resident users:{" "}
                      {users.filter((u) => u.role === "Resident").length} /{" "}
                      {residentDashboardCount}
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-slate-300">
                      <input
                        type="checkbox"
                        checked={state.formData.isWaitlist}
                        onChange={handleWaitlistChange}
                        className="rounded text-blue-500 focus:ring-blue-500"
                      />
                      Add to Waitlist
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-4 justify-center sm:justify-end">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="px-6 py-2 bg-slate-800/50 text-slate-300 rounded-lg hover:bg-slate-800 transition-colors w-full sm:w-auto"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition-colors shadow-lg hover:shadow-blue-500/25 w-full sm:w-auto"
                    >
                      Submit Intake Form
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
          {isModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[200]">
              <div className="bg-slate-900 p-6 rounded-lg shadow-lg max-w-md w-full border border-slate-800">
                <h2 className="text-white text-lg font-semibold mb-4">
                  Add New Referral Source
                </h2>
                <input
                  type="text"
                  value={newReferral}
                  onChange={(e) => setNewReferral(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter new referral source"
                />
                <div className="flex justify-end gap-4 mt-4">
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      setNewReferral(""); // Clear input
                    }}
                    className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 border border-slate-700 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleAddReferralOption(newReferral);
                      setIsModalOpen(false);
                      setNewReferral("");
                    }}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default IntakeForm;
